/**
 * WALKING THE REVIEW QUEUE FROM INSIDE A TICKET (P31, Roman's decision 4)
 *
 * Working `/review` meant open a ticket, answer it, press Back, find your place
 * again, open the next one. With 32 tickets in the queue that is 32 round trips
 * through a table that has just re-sorted itself under you, because answering a
 * ticket changes the very column the queue is ordered by.
 *
 * =========================================================================
 * THE ORDER COMES FROM WHERE THE READER CAME FROM, NOT FROM A GUESS
 * =========================================================================
 * The row click hands this screen the review board's own URL as router state
 * (`TicketTable`'s `origin` prop). That URL carries BOTH halves of P30's state:
 * the eight server parameters that decide which rows, and `sort=` / `f.*` that
 * decide the order and which of the loaded rows survive. So this component does
 * not reconstruct a queue — it replays the reader's, through the same three
 * pieces the table itself uses:
 *
 *   `readFilters` + `toQueryString` -> the identical query string, which means
 *       react-query serves it from the cache the board just filled: no refetch,
 *       no flash, and by construction the same rows;
 *   `useColumnFilters` over `clientFilterColumns` -> the same client-side
 *       narrowing;
 *   `sortRows` over `ticketSortAccessors` -> the same comparator, extracted from
 *       `useTableSort` for exactly this reason. A second implementation here
 *       would drift on the cases the rules exist for (nils last, numeric-aware
 *       strings) and "next" would quietly mean something else than the arrow the
 *       reader is looking at.
 *
 * =========================================================================
 * WHEN THE TICKET IS NO LONGER IN THE QUEUE
 * =========================================================================
 * The common case, not an edge one: answering a question takes the ticket out of
 * review, and the reader is then standing on a ticket the queue no longer
 * contains. That is reported rather than hidden — "no longer in the review
 * queue", with the way back still there. Inventing a neighbour for a row that is
 * not in the list would be guessing where the reader was.
 *
 * Without router state (a reload, a bookmark, a link from a mail) this component
 * is not rendered at all and the ticket screen keeps its plain "Back to tickets"
 * link. The state is deliberately not persisted: which queue somebody was
 * walking ten minutes ago is not a fact about this ticket.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTicketsList } from '../hooks/useTicketsApi';
import { readFilters, readView, toQueryString } from '../hooks/useTicketFilters';
import { useColumnFilters } from '../hooks/useColumnFilters';
import { sortRows } from '../hooks/useTableSort';
import { trackFeature } from '../services/usage';
import { REVIEW_DEFAULT_SORT, clientFilterColumns, ticketSortAccessors } from './TicketTable';

/** True for an origin this component knows how to walk. */
export function isReviewOrigin(origin: unknown): origin is string {
    return typeof origin === 'string' && origin.startsWith('/review');
}

export default function ReviewQueueNav({ ticketNumber, origin }: { ticketNumber: string; origin: string }) {
    const params = useMemo(() => new URLSearchParams(origin.split('?')[1] ?? ''), [origin]);
    const filters = useMemo(() => readFilters(params), [params]);
    const view = useMemo(() => readView(params), [params]);

    // The same query string the board sent, so this is a cache read.
    const { data: result } = useTicketsList(toQueryString(filters, true));
    const tickets = useMemo(() => result?.data ?? [], [result]);

    const now = Date.now();
    const filterColumns = useMemo(() => clientFilterColumns(now, true), [now]);
    const { filtered } = useColumnFilters(tickets, filterColumns, view.columnFilters);
    const ordered = useMemo(
        () => sortRows(filtered, ticketSortAccessors(now), view.sort ?? REVIEW_DEFAULT_SORT),
        [filtered, view.sort, now],
    );

    const index = ordered.findIndex(t => t.ticketNumber === ticketNumber);
    const previous = index > 0 ? ordered[index - 1] : null;
    const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null;

    /** Every step keeps walking the SAME queue — `state` travels with the link. */
    const step = (to: { ticketNumber: string }) => `/tickets/${to.ticketNumber}`;

    return (
        <div className="flex flex-wrap items-center gap-3 text-sm">
            <Link to={origin} className="text-info hover:underline">&larr; Back to review</Link>

            {index >= 0 ? (
                <>
                    <span className="text-muted">|</span>
                    <span className="text-gray-500" title="Your position in the review queue as you left it — the filters, the column filters and the sort of the board you came from.">
                        {index + 1} of {ordered.length} in review
                    </span>
                    <span className="inline-flex items-center gap-1">
                        {previous ? (
                            <Link
                                to={step(previous)}
                                state={{ origin }}
                                onClick={() => trackFeature('review-queue-previous')}
                                title={`Previous in review: ${previous.ticketNumber}`}
                                className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                            >
                                <ChevronLeft className="h-3 w-3" /> Previous
                            </Link>
                        ) : (
                            <span className="inline-flex items-center rounded border border-gray-200 px-2 py-1 text-xs text-gray-300" title="This is the first ticket in the queue.">
                                <ChevronLeft className="h-3 w-3" /> Previous
                            </span>
                        )}
                        {next ? (
                            <Link
                                to={step(next)}
                                state={{ origin }}
                                onClick={() => trackFeature('review-queue-next')}
                                title={`Next in review: ${next.ticketNumber}`}
                                className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                            >
                                Next <ChevronRight className="h-3 w-3" />
                            </Link>
                        ) : (
                            <span className="inline-flex items-center rounded border border-gray-200 px-2 py-1 text-xs text-gray-300" title="This is the last ticket in the queue.">
                                Next <ChevronRight className="h-3 w-3" />
                            </span>
                        )}
                    </span>
                </>
            ) : (
                <>
                    <span className="text-muted">|</span>
                    <span
                        className="text-gray-500"
                        title="It has left the queue — usually because the question was just answered. The list behind the link no longer contains it, so there is no next ticket to point at."
                    >
                        no longer in the review queue
                    </span>
                </>
            )}
        </div>
    );
}
